import { useEffect, useRef, useState } from 'react';
import { Loader } from 'lucide-react';

import { productionApi } from 'api/production';
import { Button } from 'shadcn/components/ui/Button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from 'shadcn/components/ui/Dialog';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';
import { MultiSelect } from 'shadcn/components/ui/MultiSelect';
import { getWeekNumber, getWeekYear } from 'utils/dateUtils/dateUtils';

import DateWeekInput from './DateWeekInput';
import PackGroupInput from './PackGroupInput';

function DuplicateDialog({ configId, initialDate, initialName, isLoading, isOpen, onClose, onConfirm }) {
    const [name, setName] = useState(initialName ?? '');

    const [date, setDate] = useState(initialDate ?? new Date());
    const [datePopoverOpen, setDatePopoverOpen] = useState(false);

    const [packGroups, setPackGroups] = useState();
    const [selectedPackGroupIDs, setSelectedPackGroupIDs] = useState([]);
    const multiSelectRef = useRef(null);

    const dialogRef = useRef(null);

    const clearSelectedPackGroups = () => {
        if (multiSelectRef.current) {
            setSelectedPackGroupIDs([]);
            multiSelectRef.current.clearValues();
        }
    };

    useEffect(() => {
        if (!configId) return;

        setName(initialName);
        setDate(initialDate);
        clearSelectedPackGroups();
    }, [configId, initialDate, initialName]);

    useEffect(() => {
        const fetchPackGroups = async () => {
            try {
                const response = await productionApi.getPackGroups({
                    week: getWeekNumber(date, 'YYYY-MM-DD'),
                    year: getWeekYear(date, 'YYYY-MM-DD'),
                });
                const packGroups = response.data.packGroups.map((packGroup) => ({
                    label: packGroup.name,
                    value: packGroup.id,
                }));
                setPackGroups(packGroups);
                clearSelectedPackGroups();
            } catch (error) {
                console.error('Error fetching pack groups:', error);
            }
        };
        fetchPackGroups();
    }, [date]);

    const handleSelectDate = (date) => {
        setDate(date);
        setDatePopoverOpen(false);
    };

    const handleConfirm = () => {
        onConfirm(name, selectedPackGroupIDs);
    };

    return (
        <Dialog open={isOpen} onOpenChange={isLoading ? null : onClose}>
            <DialogContent ref={dialogRef}>
                <DialogHeader>
                    <DialogTitle>Duplicate Configuration</DialogTitle>
                </DialogHeader>
                <DialogDescription>
                    This action will create a new configuration with the same content within the specified pack group.
                </DialogDescription>
                <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-2">
                        <Label htmlFor={`name-${configId}`}>Name</Label>
                        <Input
                            type="text"
                            id={`name-${configId}`}
                            name="name"
                            value={name}
                            placeholder="Enter name"
                            onChange={(e) => setName(e.target.value)}
                            required
                        />
                    </div>

                    <DateWeekInput
                        date={date}
                        onSelectDate={handleSelectDate}
                        popoverOpen={datePopoverOpen}
                        setPopoverOpen={setDatePopoverOpen}
                    />

                    <div className="flex flex-col gap-2">
                        <Label htmlFor="packGroups-${configId}">Pack Groups</Label>
                        <MultiSelect
                            ref={multiSelectRef}
                            id={`packGroups-${configId}`}
                            maxCount={Infinity}
                            modalPopover
                            options={packGroups}
                            placeholder="None"
                            onValueChange={(packGroup) => setSelectedPackGroupIDs(packGroup)}
                            defaultValue={[]}
                            container={dialogRef.current}
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button onClick={handleConfirm} disabled={isLoading}>
                        {isLoading ? (
                            <>
                                <Loader className="w-4 h-4 mr-1 animate-spin" />
                                Duplicating...
                            </>
                        ) : (
                            'Confirm'
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default DuplicateDialog;
